import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DollarSign } from 'lucide-react';
import type { ClientFormData } from './utilities/formDataUtils';
import type { BreakBillingPolicy } from '@/types/MealBreakConfig';

interface ClientReviewStepProps {
  formData: ClientFormData;
  customizedSections: {
    billing: boolean;
    invoicing: boolean;
    roles: boolean;
    multipliers: boolean;
    shortTurn: boolean;
    holiday: boolean;
    mealBreak: boolean;
    overnight: boolean;
    expenses: boolean;
  };
}

const ReviewField: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({ label, value, className }) => (
  <div className={cn("flex justify-between items-start py-3 border-b last:border-b-0", className)}>
    <p className="text-sm font-medium text-muted-foreground flex-shrink-0 pr-4">{label}</p>
    <div className="text-sm text-right break-words">{value}</div>
  </div>
);

const NotSet = () => <span className="italic text-muted-foreground">Not set</span>;

const weekDayMap: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const ClientReviewStepComponent: React.FC<ClientReviewStepProps> = ({ formData, customizedSections }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="p-4">
          <CardTitle className="text-lg">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Client Name" value={formData.name || <NotSet />} />
          <ReviewField label="Description" value={formData.description || <span className="italic text-muted-foreground">No description</span>} />
          <ReviewField label="Website Domain" value={formData.domain || <NotSet />} />
          <ReviewField label="Physical Address" value={formData.physical_address || <NotSet />} />
          <ReviewField label="Billing Address" value={formData.billing_address || <NotSet />} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Billing Configuration</CardTitle>
          <Badge variant={customizedSections.billing ? 'default' : 'secondary'}>
            {customizedSections.billing ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Minimum Billable Hours" value={formData.minimum_billable_hours != null ? `${formData.minimum_billable_hours} hours` : <NotSet />} />
          <ReviewField label="Half Day Threshold" value={formData.half_day_hours != null ? `${formData.half_day_hours} hours` : <NotSet />} />
          <ReviewField label="Break Billing" value={
            formData.break_billing_policy
              ? (() => {
                  const bbp = formData.break_billing_policy as BreakBillingPolicy;
                  return bbp.thresholdMinutes != null
                    ? <span>≤{bbp.thresholdMinutes}min Paid, &gt;{bbp.thresholdMinutes}min Unpaid</span>
                    : <span>{bbp.upToThreshold === 'paid' ? 'All Paid' : 'All Unpaid'}</span>;
                })()
              : <NotSet />
          } />
          <ReviewField 
            label="Work Week" 
            value={ 
              formData.week_start != null
              ? `Starts on ${weekDayMap[formData.week_start] || 'Invalid Day'}` : <NotSet />
            } 
          />
          <ReviewField
            label="Overlapping Overtime"
            value={
              <span className="capitalize">
                {formData.overtime_concurrency_strategy?.replace(/_/g, ' ') || 'Highest Prevailing'}
              </span>
            }
          />
          <h4 className="text-md font-semibold pt-4 pb-2 border-t mt-3">Travel</h4>
          <ReviewField label="Per Diem Rate" value={formData.per_diem_base_rate != null ? `$${formData.per_diem_base_rate}` : <NotSet />} />
          <ReviewField label="Travel Day Multiplier" value={formData.travel_day_rate_multiplier != null ? `${formData.travel_day_rate_multiplier}x` : <NotSet />} />
          <ReviewField label="Travel Overages" value={formData.multiplier_applies_to_overages ? 'Multiplier applies' : 'Multiplier does not apply'} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Meal Break Rules</CardTitle>
          <Badge variant={customizedSections.mealBreak ? 'default' : 'secondary'}>
            {customizedSections.mealBreak ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Apply Meal Surcharge" value={formData.apply_meal_surcharge ? 'Yes' : 'No'} />
          {formData.apply_meal_surcharge && (
            <>
              <ReviewField label="Meal Deadline" value={formData.meal_deadline_hours != null ? `${formData.meal_deadline_hours} hours` : <NotSet />} />
              <ReviewField label="Apply on Half-Days" value={formData.meal_apply_on_half_days ? 'Yes' : 'No'} />
              <ReviewField label="Surcharge Calculation" value={formData.meal_surcharge_type ? formData.meal_surcharge_type.replace(/_/g, ' ') : <NotSet />} />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Overnight Premium</CardTitle>
          <Badge variant={customizedSections.overnight ? 'default' : 'secondary'}>
            {customizedSections.overnight ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Apply Overnight Premium" value={formData.apply_overnight_premium ? 'Yes' : 'No'} />
          {formData.apply_overnight_premium && (
            <>
              <ReviewField label="Premium Window" value={formData.overnight_start_time && formData.overnight_end_time ? `${formData.overnight_start_time} - ${formData.overnight_end_time}` : <NotSet />} />
              <ReviewField label="Apply on Half-Days" value={formData.overnight_apply_on_half_days ? 'Yes' : 'No'} />
              <ReviewField 
                label="Minimum Threshold" 
                value={
                  formData.overnight_threshold_enabled 
                    ? `Yes, ${formData.overnight_threshold_type === 'percentage' 
                        ? `${formData.overnight_percentage_threshold}% of shift` 
                        : `${formData.overnight_hours_threshold} hours`}`
                    : 'No'
                } 
              />
              <ReviewField label="Premium Calculation" value={formData.overnight_premium_type ? formData.overnight_premium_type.replace(/_/g, ' ') : <NotSet />} />

              {formData.overnight_premium_type === 'credit_extra_time' && (
                <ReviewField label="Credit Minutes" value={formData.overnight_time_credit_minutes ? `${formData.overnight_time_credit_minutes} mins` : <NotSet />} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Invoicing Rules</CardTitle>
          <Badge variant={customizedSections.invoicing ? 'default' : 'secondary'}>
            {customizedSections.invoicing ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="AP Email" value={formData.accounts_payable_email || <NotSet />} />
          <ReviewField label="Payment Terms" value={formData.payment_terms_days != null ? `${formData.payment_terms_days} days` : <NotSet />} />
          <ReviewField label="Employment Type" value={formData.employment_type || <NotSet />} />
          <ReviewField label="Auto-send Reminders" value={formData.auto_send_reminders ? 'Yes' : 'No'} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Roles & Rates</CardTitle>
          <Badge variant={customizedSections.roles ? 'default' : 'secondary'}>
            {customizedSections.roles ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {formData.roles && formData.roles.length > 0 ? (
            formData.roles.map((role, index) => (
              <div key={index} className="flex justify-between items-center py-3 border-b last:border-b-0">
                <div className="flex items-center gap-2">
                  {role.billingStructure === 'day_rate' && (
                    <Badge variant={role.allowHalfDay ? 'default' : 'outline'} className="text-xs">{role.allowHalfDay ? 'Half Day OK' : 'No Half Days'}</Badge>
                  )}
                  <p className="font-medium">{role.name}</p>
                </div>
                <div className="text-sm text-right flex items-center gap-1 text-muted-foreground">
                  <DollarSign className="h-3.5 w-3.5" />
                  <span>
                    {role.rate.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace('.00', '')}
                    <span className="text-xs">/{role.billingStructure === 'day_rate' ? 'day' : 'hr'}</span>
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground italic">No roles defined.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Multipliers & Short Turn</CardTitle>
          <Badge variant={customizedSections.multipliers ? 'default' : 'secondary'}>
            {customizedSections.multipliers ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {formData.rate_multipliers && formData.rate_multipliers.length > 0 ? (
            formData.rate_multipliers.map((multiplier, index) => (
              <div key={index} className="flex justify-between items-center py-3 border-b last:border-b-0">
                <p className="font-medium">{multiplier.name}</p>
                <div className="text-sm text-right flex items-center gap-2 text-muted-foreground">
                  <span>{multiplier.defaultMultiplier}x</span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground italic">No multipliers defined.</p>
          )}
          <ReviewField label="Uses Short Turn" value={formData.uses_short_turn ? 'Yes' : 'No'} />
          {formData.uses_short_turn && (
            <>
              <ReviewField label="Apply on Half-Days" value={formData.short_turn_apply_on_half_days ? 'Yes' : 'No'} />
              <ReviewField 
                label="Surcharge Type" 
                value={formData.short_turn_surcharge_type ? <span className="capitalize">{formData.short_turn_surcharge_type.replace(/_/g, ' ')}</span> : <NotSet />} 
              />
              {formData.short_turn_surcharge_type === 'hourly_premium' && (
                <ReviewField
                    label="Uplift"
                    value={
                        formData.short_turn_premium_type === 'percentage'
                        ? `${formData.short_turn_premium_value || 0}%`
                        : `$${formData.short_turn_premium_value || 0}/hr`
                    }
                />
              )}
              {formData.short_turn_surcharge_type === 'fixed_dollar' && (
                <ReviewField label="Surcharge Amount" value={formData.short_turn_fixed_surcharge_amount != null ? `$${formData.short_turn_fixed_surcharge_amount}` : <NotSet />} />
              )}
              {formData.short_turn_surcharge_type === 'offset_overtime' && (
                <ReviewField label="Offset Amount" value={formData.short_turn_overtime_offset != null ? `${formData.short_turn_overtime_offset} tier(s)` : <NotSet />} />
              )}
              {formData.short_turn_surcharge_type === 'guaranteed_rate' && (
                <ReviewField label="Guaranteed Rate" value={formData.rate_multipliers?.find(m => m.code === formData.short_turn_floor_tier_code)?.name || <NotSet />} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Holiday Premium</CardTitle>
          <Badge variant={customizedSections.holiday ? 'default' : 'secondary'}>
            {customizedSections.holiday ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Apply Holiday Premium" value={formData.apply_holiday_premium ? 'Yes' : 'No'} />
          {formData.apply_holiday_premium && (
            <>
              <ReviewField label="Selected Holidays" value={`${((formData.catalog_holiday_ids?.length ?? 0) + (formData.custom_holiday_ids?.length ?? 0))} selected`} />
              <ReviewField label="Apply on Half-Days" value={formData.holiday_apply_on_half_days ? 'Yes' : 'No'} />
              <ReviewField 
                label="Surcharge Type" 
                value={formData.holiday_premium_type ? <span className="capitalize">{formData.holiday_premium_type.replace(/_/g, ' ')}</span> : <NotSet />} 
              />
              {formData.holiday_premium_type === 'hourly_premium' && (
                <ReviewField
                  label="Uplift"
                  value={
                    formData.holiday_hourly_premium_type === 'percentage'
                    ? `${formData.holiday_hourly_premium_value || 0}%`
                    : `$${formData.holiday_hourly_premium_value || 0}/hr`
                  }
                />
              )}
              {formData.holiday_premium_type === 'fixed_dollar' && (
                <ReviewField label="Surcharge Amount" value={formData.holiday_fixed_premium_amount != null ? `$${formData.holiday_fixed_premium_amount}` : <NotSet />} />
              )}
              {formData.holiday_premium_type === 'offset_overtime' && (
                <ReviewField label="Offset Amount" value={(formData as Record<string, unknown>).holiday_overtime_offset != null ? `${(formData as Record<string, unknown>).holiday_overtime_offset} tier(s)` : <NotSet />} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4">
          <CardTitle className="text-lg">Expense Policies</CardTitle>
          <Badge variant={customizedSections.expenses ? 'default' : 'secondary'}>
            {customizedSections.expenses ? 'Custom' : 'Using Profile Defaults'}
          </Badge>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ReviewField label="Mileage Rate" value={formData.expense_mileage_rate != null ? `$${formData.expense_mileage_rate}/${formData.expense_mileage_unit || 'unit'}` : <NotSet />} />
          <ReviewField label="Receipts Required Above" value={formData.expense_receipt_required_above_amount != null ? `$${formData.expense_receipt_required_above_amount}` : <NotSet />} />
          <ReviewField 
            label="Default Markup" 
            value={
              formData.default_expense_markup_type === 'percentage' 
                ? `${formData.default_expense_markup_percentage || 0}%` 
                : formData.default_expense_markup_type === 'fixed_amount'
                ? `$${formData.default_expense_markup_fixed_amount || 0}`
                : 'None'
            } 
          />
          <ReviewField label="Base Categories" value={`${formData.expense_policies?.base_policy?.allowedCategories?.length || 0} allowed`} />
          {formData.expense_policies?.travel_policy && (
            <ReviewField label="Travel Categories" value={`${formData.expense_policies.travel_policy.allowedCategories?.length || 0} allowed`} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const ClientReviewStep = React.memo(ClientReviewStepComponent);
